'use client';

import * as React from 'react';
import { useMemo, useState, useCallback, useEffect } from 'react';

import { useTranslations } from 'next-intl';

import { CloseButton } from '@/components/overlay/CloseButton';
import { SmartModal } from '@/components/overlay/SmartModal';
import { PaginationDefault } from '@/components/shared/PaginationDefault';
import { P } from '@/components/shared/Typography';
import { Button } from '@/components/ui/button';
import { I_Avatar, I_AvatarTabs } from '@/data/editProfileMock';
import { useDeviceDetection } from '@/hooks/useDeviceDetection';
import { C_AVATARS_PER_PAGE } from '@/ts/constants/C_AVATARS';

import { AvatarPreviewDialog } from '../AvatarPreviewDialog';

import { AvatarGrid, GenderToggle, RaceTabs } from './index';

type T_Gender = 'men' | 'women';
type T_Race = 'african' | 'arabic' | 'asian' | 'caucasian' | 'indian';

type T_SmartChooseAvatarDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  trigger?: React.ReactNode;
  selectedAvatar?: I_Avatar | null;
  onAvatarSelect?: (avatar: I_Avatar) => void;
  isUploading?: boolean;
  avatars?: I_AvatarTabs | null;
};

export function ChooseAvatarDialog({
  open,
  onOpenChange,
  trigger,
  selectedAvatar,
  onAvatarSelect,
  isUploading = false,
  avatars
}: T_SmartChooseAvatarDialogProps) {
  const t = useTranslations('editProfile');
  const { isMobile } = useDeviceDetection();

  const [activeGender, setActiveGender] = useState<T_Gender>('women');
  const [activeRace, setActiveRace] = useState<T_Race>('african');
  const [currentSelectedAvatar, setCurrentSelectedAvatar] =
    useState<I_Avatar | null>(selectedAvatar || null);
  // Combined state: when previewAvatar is set, dialog is open; when null, dialog is closed
  const [previewAvatar, setPreviewAvatar] = useState<I_Avatar | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const avatarData = avatars;

  const availableRaces = useMemo(() => {
    if (!avatarData) return [];
    const genderData = avatarData[activeGender];
    return Object.keys(genderData).map(race => ({
      category: race as T_Race,
      label: race.charAt(0).toUpperCase() + race.slice(1)
    }));
  }, [activeGender, avatarData]);

  const currentAvatars = useMemo(() => {
    if (!avatarData) return [];
    const genderData = avatarData[activeGender];
    return genderData[activeRace] || [];
  }, [activeGender, activeRace, avatarData]);

  // Pagination logic
  const itemsPerPage = isMobile
    ? C_AVATARS_PER_PAGE.mobile
    : C_AVATARS_PER_PAGE.desktop;
  const totalPages = Math.ceil(currentAvatars.length / itemsPerPage);
  const paginatedAvatars = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return currentAvatars.slice(startIndex, endIndex);
  }, [currentAvatars, currentPage, itemsPerPage]);

  const handleGenderChange = useCallback(
    (gender: T_Gender) => {
      setActiveGender(gender);
      setCurrentPage(1); // Reset to first page when gender changes

      if (!avatarData) return;
      const newGenderRaces = Object.keys(avatarData[gender]);
      if (newGenderRaces.includes(activeRace)) {
        return;
      } else {
        const firstRace = newGenderRaces[0] as T_Race;
        if (firstRace) {
          setActiveRace(firstRace);
        }
      }
    },
    [avatarData, activeRace]
  );

  const handleRaceChange = useCallback((race: T_Race) => {
    setActiveRace(race);
    setCurrentPage(1);
  }, []);

  // Reset to first page when device type changes (mobile/desktop)
  useEffect(() => {
    setCurrentPage(1);
  }, [isMobile]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleAvatarSelect = useCallback((avatar: I_Avatar) => {
    setCurrentSelectedAvatar(avatar);
  }, []);

  const handlePreview = useCallback((avatar: I_Avatar) => {
    setPreviewAvatar(avatar); // Setting avatar automatically opens the dialog
  }, []);

  const handlePreviewSelect = useCallback((avatar: I_Avatar) => {
    setCurrentSelectedAvatar(avatar);
    setPreviewAvatar(null); // Setting to null automatically closes the dialog
  }, []);

  const handlePreviewClose = useCallback((open: boolean) => {
    if (!open) {
      setPreviewAvatar(null); // Close by setting to null
    }
  }, []);

  const handleSave = useCallback(() => {
    if (currentSelectedAvatar && onAvatarSelect) {
      onAvatarSelect(currentSelectedAvatar);
    }
    onOpenChange(false);
  }, [currentSelectedAvatar, onAvatarSelect, onOpenChange]);

  return (
    <SmartModal
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      variant="glass"
      dialogType="dialog"
      a11yTitle={t('accessibility.chooseAvatar')}
      showClose={false}
      className="w-full md:min-w-182"
    >
      <div className="w-full flex flex-col items-center predictionPoolModal overflow-hidden overflow-y-scroll">
        <div className="flex justify-between items-center w-full p-7 border-b-1 ">
          <P className="text-lg text-left font-bold ">{t('avatar.title')}</P>
          <CloseButton className="flex items-center justify-center w-8 h-8 relative  right-0 top-0 " />
        </div>
        <div className="w-full max-w-full flex flex-col gap-2.5 justify-center p-7 pb-0">
          <GenderToggle
            activeGender={activeGender}
            onGenderChange={handleGenderChange}
          />
          <div className="overflow-x-auto max-w-full ">
            <RaceTabs
              activeRace={activeRace}
              onRaceChange={handleRaceChange}
              races={availableRaces}
            />
          </div>
        </div>
        <div className="p-7 ">
          <AvatarGrid
            avatars={paginatedAvatars}
            selectedAvatar={currentSelectedAvatar}
            onAvatarSelect={handleAvatarSelect}
            onPreview={handlePreview}
            isLoading={!avatars}
          />
        </div>

        {/* Pagination with spacing */}
        {totalPages > 1 && (
          <div className="w-full p-7">
            <PaginationDefault
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        <div className="flex w-full p-7 justify-end border-t gap-3">
          <Button
            onClick={handleSave}
            disabled={!currentSelectedAvatar}
            size="lg"
            className="flex-1"
          >
            {t('avatar.save')}
          </Button>
        </div>

        <AvatarPreviewDialog
          key={previewAvatar?.uuid || 'no-preview'}
          open={!!previewAvatar}
          onOpenChange={handlePreviewClose}
          avatars={paginatedAvatars}
          selectedAvatar={previewAvatar}
          onSelectAvatar={handlePreviewSelect}
          isUploading={isUploading}
        />
      </div>
    </SmartModal>
  );
}
